import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { restoreSession, selectAuth, selectCan, selectIsSuperAdmin } from './store/authSlice';
import { loadMenu, loadLookups } from './store/menuSlice';
import { loadShift, loadFiscalStatus } from './store/uiSlice';

import AppLayout from './layout/AppLayout';
import LoginPage from './pages/LoginPage';
import PosPage from './pages/PosPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import TablesPage from './pages/TablesPage';
import MenuItemsPage from './pages/MenuItemsPage';
import ModifiersPage from './pages/ModifiersPage';
import CustomersPage from './pages/CustomersPage';
import InventoryPage from './pages/InventoryPage';
import PurchasesPage from './pages/PurchasesPage';
import SuppliersPage from './pages/SuppliersPage';
import RecipesPage from './pages/RecipesPage';
import ReportsPage from './pages/ReportsPage';
import FiscalPage from './pages/FiscalPage';
import AccountsPage from './pages/AccountsPage';
import EmployeesPage from './pages/EmployeesPage';
import SettingsPage from './pages/SettingsPage';
import RolesPage from './pages/RolesPage';
import Spinner from './components/ui/Spinner';

/** A route only the holder of `permission` may open. */
const Protected = ({ permission, children }) => {
    const can = useSelector(selectCan);
    const location = useLocation();

    if (permission && !can(permission)) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                <h2 className="text-lg font-semibold text-slate-700">
                    You do not have access to this screen
                </h2>
                <p className="max-w-sm text-sm text-slate-500">
                    Ask a manager to grant the
                    {' '}<code className="rounded bg-slate-100 px-1.5 py-0.5">{permission}</code>{' '}
                    permission to your role.
                </p>
                <p className="text-xs text-slate-400">{location.pathname}</p>
            </div>
        );
    }

    return children;
};

/**
 * A route only the supplier's Super Admin may open.
 *
 * Separate from Protected because that one passes an Owner on every permission
 * code - correct for the shop's own screens, wrong for the screen that decides
 * what those screens are. The API refuses these calls independently, so this
 * only saves an Owner from a page of failed requests.
 */
const SuperAdminOnly = ({ children }) => {
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    if (!isSuperAdmin) return <Navigate to="/pos" replace />;
    return children;
};

const App = () => {
    const dispatch = useDispatch();
    const { status, isRestoring } = useSelector(selectAuth);
    const isAuthenticated = status === 'authenticated';

    // Restore a session from the stored token before deciding what to render,
    // so a reload does not bounce a signed-in cashier to the login screen.
    useEffect(() => { dispatch(restoreSession()); }, [dispatch]);

    // Once signed in, load everything the shell needs. The menu and lookups are
    // held in memory for the session; the shift and fiscal badge are refreshed
    // periodically below.
    useEffect(() => {
        if (!isAuthenticated) return;
        dispatch(loadLookups());
        dispatch(loadMenu());
        dispatch(loadShift());
        dispatch(loadFiscalStatus());
    }, [isAuthenticated, dispatch]);

    /**
     * Poll the fiscal queue.
     *
     * The badge tells the owner that invoices are stacking up because the shop
     * has been offline - which is exactly the thing nobody notices until a
     * month of sales are unreported. A minute is often enough to be useful and
     * rare enough to be free.
     */
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const timer = setInterval(() => dispatch(loadFiscalStatus()), 60000);
        return () => clearInterval(timer);
    }, [isAuthenticated, dispatch]);

    if (isRestoring) {
        return (
            <div className="flex h-full items-center justify-center bg-slate-100">
                <Spinner size="lg" label="Starting up…" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }

    return (
        <Routes>
            <Route path="/login" element={<Navigate to="/pos" replace />} />

            <Route element={<AppLayout />}>
                <Route path="/pos" element={<Protected permission="POS"><PosPage /></Protected>} />
                <Route path="/dashboard" element={<Protected permission="DASHBOARD"><DashboardPage /></Protected>} />
                <Route path="/orders" element={<Protected permission="ORDERS"><OrdersPage /></Protected>} />
                <Route path="/tables" element={<Protected permission="TABLES"><TablesPage /></Protected>} />
                <Route path="/menu-items" element={<Protected permission="MENU_ITEMS"><MenuItemsPage /></Protected>} />
                <Route path="/modifiers" element={<Protected permission="MODIFIERS"><ModifiersPage /></Protected>} />
                <Route path="/customers" element={<Protected permission="CUSTOMERS"><CustomersPage /></Protected>} />
                <Route path="/inventory" element={<Protected permission="INVENTORY"><InventoryPage /></Protected>} />
                <Route path="/purchases" element={<Protected permission="PURCHASES"><PurchasesPage /></Protected>} />
                <Route path="/suppliers" element={<Protected permission="SUPPLIERS"><SuppliersPage /></Protected>} />
                <Route path="/recipes" element={<Protected permission="RECIPES"><RecipesPage /></Protected>} />
                <Route path="/reports" element={<Protected permission="REPORTS"><ReportsPage /></Protected>} />
                <Route path="/accounts" element={<Protected permission="ACCOUNTS"><AccountsPage /></Protected>} />
                <Route path="/fiscal" element={<Protected permission="FISCAL"><FiscalPage /></Protected>} />
                <Route path="/employees" element={<Protected permission="EMPLOYEES"><EmployeesPage /></Protected>} />
                <Route path="/settings" element={<Protected permission="SETTINGS"><SettingsPage /></Protected>} />
                <Route path="/roles" element={<SuperAdminOnly><RolesPage /></SuperAdminOnly>} />
            </Route>

            {/* The till is where a cashier belongs, so that is the landing page. */}
            <Route path="*" element={<Navigate to="/pos" replace />} />
        </Routes>
    );
};

export default App;
